/**
 * L1 §1 — the site heat strip. One `StatusStrip` row per site, one cell per
 * day. This is the flagship view: it replaces reading a month sheet of the
 * spreadsheet, and it is the only place where a site that *quietly stopped
 * reporting* is visible at a glance (concept §2.3, §4).
 *
 * The honesty that matters here is the grey cell. The backend marks a day with
 * no recorded run as `unknown`, and that must render as a visible grey bar —
 * never as absence (which reads as "fine") and never as green. That is the
 * whole failure mode the spreadsheet has: a missed day is invisible.
 *
 * WORKAROUND (design-system gap, the same two as `features/dashboard/uptime-heatmap.tsx`):
 * `StatusStrip` is built for ~12 labelled cells carrying values, so a 30-day
 * row of empty cells needs nudging.
 *   1. `h-8` — cells size from their content, so an unlabelled row collapses
 *      to a few pixels.
 *   2. `bg-border` on `neutral` — the DS neutral tone is `bg-muted`, nearly
 *      indistinguishable from the card surface on the dark theme, which would
 *      leave "no data" invisible rather than reading as a grey bar.
 * Both are token-backed Tailwind utilities passed through `className` (DS CSS
 * ships in `@layer norican`, so utilities win) — no DS CSS is forked. If
 * `StatusStrip` gains a dense variant and a visible neutral tone, drop both.
 *
 * Interaction: the *row* is the door, not the cell. A day cell would have to
 * drill into a historical job list, and the job endpoint carries current state
 * only — a cell from three weeks ago linking to today's failures would be a
 * lie. So each site name drills to its jobs, and every charted value is
 * reachable through the card's table twin instead (design rules §4.4).
 */

import { cn, EmptyState, StatusStrip, Typography } from "@norican/react";

import { ChartCard } from "@/components/board";
import type { BackupSiteRow, BackupSites } from "@/lib/api";
import { formatCount } from "@/lib/charts/config";
import { DrillLink } from "@/lib/drill";

import { BACKUP_JOBS_PATH, SITE_PARAM, SOURCE_PARAM } from "./params";
import { type CellStatus, dayStatus, dayTitle, formatDay, sourceLabel } from "./status";

/** One row of the twin table: a site-day, flattened. */
interface SiteDayRow {
  id: string;
  site: string;
  day: string;
  status: string;
  total: number;
  succeeded: number;
  warning: number;
  failed: number;
}

interface SiteHeatStripProps {
  sites: BackupSites;
  /** Selected sources from the board FilterRow ([] = all). */
  sourceIds: string[];
  /** Selected sites from the board FilterRow ([] = all). */
  siteIds: string[];
}

export function SiteHeatStrip({ sites, sourceIds, siteIds }: SiteHeatStripProps) {
  const rows = sites.sites.filter(
    (site) =>
      (siteIds.length === 0 || siteIds.includes(site.site)) &&
      (sourceIds.length === 0 || site.sources.some((source) => sourceIds.includes(source))),
  );

  const twinRows: SiteDayRow[] = rows.flatMap((site) =>
    site.days.map((day) => ({
      id: `${site.site}:${day.day}`,
      site: site.site,
      day: day.day,
      status: day.status,
      total: day.total,
      succeeded: day.succeeded,
      warning: day.warning,
      failed: day.failed,
    })),
  );

  // Sites that reported nothing across the whole window — the finding this
  // panel exists to surface, stated in words as well as in grey.
  const silent = rows.filter((site) => site.silent_days === site.days.length);

  return (
    <section id="backup-heat" className="scroll-mt-6">
      <ChartCard<SiteDayRow>
        title={`${sites.days}-day backup outcome by site`}
        caption={
          silent.length > 0
            ? `${formatCount(silent.length)} of ${formatCount(rows.length)} sites reported nothing in this window`
            : `${formatCount(rows.length)} sites · one cell per day, oldest on the left`
        }
        since={sites.collected_at}
        tableTwin={{
          id: "backup-site-days",
          ariaLabel: `Backup outcomes per site per day, last ${sites.days} days`,
          data: twinRows,
          getRowId: (row) => row.id,
          columns: [
            { id: "site", header: "Site", accessor: (row) => row.site },
            {
              id: "day",
              header: "Day",
              accessor: (row) => row.day,
              cell: (row) => <span className="tabular-nums">{formatDay(row.day)}</span>,
            },
            {
              id: "status",
              header: "Worst outcome",
              accessor: (row) => row.status,
              cell: (row) => (row.status === "unknown" ? "Nothing reported" : row.status),
            },
            { id: "total", header: "Sessions", accessor: (row) => row.total, align: "end" },
            {
              id: "succeeded",
              header: "Succeeded",
              accessor: (row) => row.succeeded,
              align: "end",
            },
            { id: "warning", header: "Warnings", accessor: (row) => row.warning, align: "end" },
            { id: "failed", header: "Failed", accessor: (row) => row.failed, align: "end" },
          ],
        }}
      >
        {rows.length === 0 ? (
          <EmptyState
            size="sm"
            title="No sites match the filter"
            description="Clear the source and site filters above to see the whole estate."
          />
        ) : (
          <div className="flex flex-col gap-6">
            {rows.map((site) => (
              <SiteRow key={site.site} site={site} sourceIds={sourceIds} />
            ))}

            <div className="text-fg-muted flex items-baseline justify-between gap-4 text-xs">
              <span>{formatDay(sites.from_date)}</span>
              <span>{formatDay(sites.to_date)}</span>
            </div>

            <div className="border-border flex flex-wrap items-center gap-x-5 gap-y-2 border-t pt-4">
              <span className="text-fg-muted text-xs font-semibold tracking-wide uppercase">
                Legend
              </span>
              <LegendKey status="positive" label="Every session succeeded" />
              <LegendKey status="caution" label="Completed with warnings" />
              <LegendKey status="negative" label="A session failed" />
              <LegendKey status="neutral" label="Nothing reported" />
            </div>
          </div>
        )}
      </ChartCard>
    </section>
  );
}

function SiteRow({ site, sourceIds }: { site: BackupSiteRow; sourceIds: string[] }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <div className="flex flex-wrap items-baseline gap-3">
          <DrillLink
            to={BACKUP_JOBS_PATH}
            params={{
              [SITE_PARAM]: site.site,
              ...(sourceIds.length > 0 ? { [SOURCE_PARAM]: sourceIds } : {}),
            }}
            className="text-fg hover:text-accent text-sm font-medium underline-offset-2 hover:underline"
            aria-label={`Open the jobs at site ${site.site}`}
          >
            {site.site}
          </DrillLink>
          <span className="text-fg-muted text-xs">
            {formatCount(site.jobs)} {site.jobs === 1 ? "job" : "jobs"} ·{" "}
            {site.sources.map(sourceLabel).join(" + ") || "no source"}
          </span>
        </div>
        <Typography variant="caption" tone="muted">
          {summariseRow(site)}
        </Typography>
      </div>

      <StatusStrip aria-label={`Daily backup outcome at ${site.site}`}>
        {site.days.map((day) => {
          const status = dayStatus(day);
          return (
            <StatusStrip.Cell
              key={day.day}
              status={status}
              title={dayTitle(site.site, day)}
              // See the WORKAROUND note at the top of this file.
              className={cn("h-8", status === "neutral" && "bg-border")}
            />
          );
        })}
      </StatusStrip>
    </div>
  );
}

/** The row's own one-line verdict — silence first, because silence hides. */
function summariseRow(site: BackupSiteRow): string {
  if (site.silent_days === site.days.length) {
    return "Nothing reported in this window";
  }
  const parts: string[] = [];
  if (site.failed_days > 0) {
    parts.push(`${site.failed_days} ${site.failed_days === 1 ? "day" : "days"} with failures`);
  }
  if (site.silent_days > 0) {
    parts.push(`${site.silent_days} ${site.silent_days === 1 ? "day" : "days"} with no data`);
  }
  return parts.length > 0 ? parts.join(" · ") : "Clean";
}

function LegendKey({ status, label }: { status: CellStatus; label: string }) {
  return (
    <span className="text-fg-muted flex items-center gap-2 text-xs">
      {/* A single-cell strip reuses the exact tints the rows above use. */}
      <span className="inline-block w-8">
        <StatusStrip aria-hidden="true">
          <StatusStrip.Cell
            status={status}
            className={cn("h-3", status === "neutral" && "bg-border")}
          />
        </StatusStrip>
      </span>
      {label}
    </span>
  );
}
